import { managedFetch } from 'query/fetch';
import { AWIN_ADVERTISER_ID } from './awinConfig';
export const AWIN_CLICK_CAPTURED_SESSION_KEY = 'awin_click_captured';

/**
 * Stores the Awin click checksum through an HTTP response so it is available
 * to the API as an HTTP-only first-party cookie for later S2S conversions.
 */
export function captureAwinClick(): void {
    if (window.location.hostname !== 'app.co2impact.co.uk') return;

    const awc = new URLSearchParams(window.location.search).get('awc');
    const queryParams = new URLSearchParams(window.location.search);
    const publisherId = queryParams.get('sv_campaign_id') ?? queryParams.get('awin_publisher_id');
    const isAwinSource = [queryParams.get('source'), queryParams.get('utm_source')]
        .some((value) => value?.trim().toLowerCase() === 'awin');
    const isValidPublisherId = Boolean(publisherId && /^\d+$/.test(publisherId) && Number(publisherId) > 0);

    if (!awc
        || !isAwinSource
        || !isValidPublisherId
        || !new RegExp(`^${AWIN_ADVERTISER_ID}_[A-Za-z0-9_-]{1,240}$`).test(awc)) {
        return;
    }

    // The API cookie is HTTP-only by design, so retain only this boolean in the
    // browser. It prevents the client-side tag/fallback pixel from firing for
    // non-Awin registrations; the server still owns and validates the checksum.
    try {
        window.sessionStorage.setItem(AWIN_CLICK_CAPTURED_SESSION_KEY, '1');
    } catch {
        // S2S tracking remains the authoritative path if storage is unavailable.
    }

    const apiUrl = String(import.meta.env.VITE_API_URL || window.location.origin).replace(/\/$/, '');
    const clickEndpoint = apiUrl.endsWith('/api')
        ? `${apiUrl}/awin/click`
        : `${apiUrl}/api/awin/click`;

    const query = new URLSearchParams({
        awc,
        source: 'awin',
        sv_campaign_id: publisherId!,
    });

    void managedFetch(`${clickEndpoint}?${query.toString()}`, {
        credentials: 'include',
    }, async (response) => {
        if (!response.ok) {
            throw new Error(`Awin click capture failed with HTTP ${response.status}`);
        }
        return null;
    }).catch(() => {
        // A failed capture must never interrupt the landing page experience.
    });
}
